import { ChangeDetectorRef, Component, ElementRef, NgZone, OnDestroy, OnInit, ViewChild } from "@angular/core";
import { ActivatedRoute, Router } from "@angular/router";
import { MatDialog } from "@angular/material/dialog";
import { MatDialogRef } from "@angular/material/dialog";
import { Chart, Filler, Legend, LineElement, PointElement, RadialLinearScale, RadarController, Tooltip, } from "chart.js";
import { ModernizationReviewService, ReviewLog } from "../services/modernization-review.service";
import { CmdbConfidenceSignal, ModernizationAssessmentService } from "../services/modernization-assessment.service";
import { UserContextService } from "../services/common/user-context.service";
import { LoggedUserService } from "../services/common/logged-user.service";
import { Role } from "../models/authorization/role.enum";
import { Subject, forkJoin, of } from "rxjs";
import { takeUntil, switchMap, catchError, finalize } from "rxjs/operators";
import { DashboardService } from "../dashboard/dashboard.service";
import { ApplicationDetail } from "../dashboard/dashboard.models";
import { AiAssessmentService, AssessmentJob, AssessmentJobType } from "../services/ai-assessment.service";
import { AssignmentService, Assignment, Rejection, OverrideAssessmentPayload, ApproveAssessmentPayload, ApproveAssessmentResult, AssignPodRequest, ApplicationHistory } from "../applications/Assignment/assignment.service";
import { ApplicationHistoryDialogComponent } from "../applications/application-history/application-history-dialog.component";
import { StartAssessmentDialogComponent, StartAssessmentDialogData } from "../shared/start-assessment-dialog/start-assessment-dialog.component";
import { PERSONA_ACTIONS } from "../constants/persona-actions.constants";
import { CommonModule } from "@angular/common";
import { MaterialModule } from "../material.module";
import { AssessmentDetailSkeletonComponent } from "./assessment-detail-skeleton/assessment-detail-skeleton.component";
import { ActionDialogComponent, ActionDialogData, ActionDialogResult } from "../shared/action-dialog/action-dialog.component";
import { MessageDialogComponent, MessageDialogData } from "../shared/message-dialog/message-dialog.component";
import { PatUpdateDialogComponent, PatUpdateDialogResult } from "../shared/pat-update-dialog/pat-update-dialog.component";
import { ApplicationsStateService } from "../services/common/applications-state.service";
import { UtcDatePipe } from "../pipes/utc-date.pipe";
import { ScoringLegendDialogComponent } from "./scoring-legend-dialog/scoring-legend-dialog.component";
import { SpinnerComponent } from "../shared/spinner/spinner.component";
import { AdoProcessingDialogComponent } from "../shared/ado-processing-dialog/ado-processing-dialog.component";
import { ConfigService } from "../services/common/config.service";
import { formatUtcDate } from "../shared/date-utils";

Chart.register(
  RadarController,
  RadialLinearScale,
  PointElement,
  LineElement,
  Filler,
  Tooltip,
  Legend
);

interface ScoreTile {
  label: string;
  value: number;
  subtitle: string;
  variant: string;
}

interface AssessmentViewModel {
  application: ApplicationDetail;
  job: AssessmentJob | null;
  hasResult: boolean;
}

/** Lightweight code validation findings for a single repository of an application. */
interface LightweightValidationView {
  jobId: number;
  repositoryUrl: string;
  repositoryLabel: string;
  branch: string;
  hasResult: boolean;
  isConflict: boolean;
  isUnavailable: boolean;
  statusLabel: string;
  outcomeClass: string;
  message: string;
  recommendedAction: string;
  pipelineMaturity: string;
  technologySignals: string;
  legacyIndicatorsPreview: string;
  score: number | null;
  signals: Array<{ signal: string; evidence: string; severity: string; reason: string }>;
  scoreExplanation: string;
  confidence: string;
  runDate: string | null;
  analysisResultLabel: string;
}

@Component({
  selector: "modernization-assessment-detail",
  templateUrl: "./assessment-detail.component.html",
  styleUrls: ["./assessment-detail.component.css"],
  imports: [MaterialModule, CommonModule, UtcDatePipe, AssessmentDetailSkeletonComponent, SpinnerComponent]
})
export class AssessmentDetailComponent implements OnInit, OnDestroy {
  private static readonly RECENT_ASSESSMENT_WINDOW_DAYS = 30;
  private static readonly MAX_LIGHTWEIGHT_REPOSITORIES = 10;
  private static readonly DEFAULT_ASSESSMENT_COVERAGE_THRESHOLD_PERCENT = 60;
  private static readonly CMDB_PRELIMINARY_MESSAGE = 'Preliminary assessment based on CMDB metadata.';
  private static readonly CMDB_ONLY_DISCLAIMER_MESSAGE = 'This recommendation is based on CMDB metadata only. If CMDB data is incomplete or outdated, the recommendation may differ from the source-code assessment.';
  private static readonly LIGHTWEIGHT_UNABLE_TO_VALIDATE_MESSAGE = 'Unable to validate code because repository access is unavailable.';

  private readonly destroy$ = new Subject<void>();

  @ViewChild("radarCanvas") private readonly radarCanvas?: ElementRef<HTMLCanvasElement>;
  private radarChart?: Chart;
  private viewReady = false;

  trackedBusinessApplicationId?: number;
  id?: number;
  sysId?: string; // BusinessApplicationSysId for review operations
  isLoading = false;
  loadError?: string;

  viewModel?: AssessmentViewModel;
  scoreTiles: ScoreTile[] = [];
  cmdbConfidenceSignals: CmdbConfidenceSignal[] = [];
  dimensionItems: Array<{
    key: string;
    dimension: string;
    size: string;
    score: number;
    reasoning: string;
    reasoningBullets: string[];
    isReasoningBulletList: boolean;
    actionItems: string[];
  }> = [];

  hasRunningJob = false;
  isJobLoading = true;
  isCmdbAnalysisRefreshing = false;
  isCmdbAnalysisRefreshQueued = false;
  isDownloadingReport = false;
  isActionProcessing = false;
  actionProcessingText = 'Processing your request...';
  private adoProcessingDialogRef: MatDialogRef<AdoProcessingDialogComponent> | null = null;
  private jobPollingTimer: any = null;
  private cmdbRefreshPollingTimer: ReturnType<typeof setInterval> | null = null;

  // AI Assessment history & state
  allJobs: AssessmentJob[] = [];
  lightweightValidations: LightweightValidationView[] = [];
  expandedLightweightValidationJobIds = new Set<number>();
  isRepositoryDetailsExpanded = false;
  selectedLightweightRepositoryJobId: number | 'all' = 'all';
  selectedJobId: number | null = null;
  hasNewerAssessment = false;

  // Assignment history
  applicationHistory: ApplicationHistory[] = [];
  isHistoryLoading = true;

  // Review workflow properties
  isAMORETeam = false;
  isEnterpriseLead = false;
  isProjectLead = false;
  isArchitect = false;
  isFinalized = false;
  isAiGenerated = false;
  cmdbAnalyzedAt?: string | null;
  finalizedBy?: string;
  finalizedAt?: Date;
  finalDecisionLabel?: string; // "Finalized" or "Architect Approved"
  approvalLogs: ReviewLog[] = [];

  // PERSONA_ACTIONS for dropdown
  personaActions: { personaActionStatusValue: string }[] = [];
  loggedUserEmail?: string;

  // Rejection reasons
  rejectionReasons: any[] = [];
  sourcedFromDashboard = false;

  get application(): ApplicationDetail | undefined {
    return this.viewModel?.application;
  }

  get job(): AssessmentJob | null | undefined {
    return this.viewModel?.job;
  }

  get hasResult(): boolean {
    return this.viewModel?.hasResult ?? false;
  }

  get isFailed(): boolean {
    const status = this.viewModel?.job?.status;
    return status === 'Failed' || status === '3' || (status as any) === 3;
  }

  get codeAssessmentRepositoryUrl(): string | null {
    return this.job?.repositoryUrl ?? null;
  }

  get codeAssessmentRepositoryLabel(): string {
    return this.buildRepositoryLabel(this.job?.repositoryUrl);
  }

  get isSourceCodeAssessmentNotAvailable(): boolean {
    return !this.isJobLoading && !this.hasResult && !this.isFailed && !this.hasRunningJob;
  }

  get sourceCodeAssessmentNotAvailableMessage(): string {
    if (this.isRepositoryUnavailableForAssessment) {
      return 'Source Code Assessment Not Available: Repository is not available for this application.';
    }

    return 'Source Code Assessment Not Available: No completed source code assessment exists for this application.';
  }

  get assessmentDataSourcesLabel(): string {
    const sources = ['CMDB Metadata'];

    if (this.hasLightweightValidationResult || this.hasLightweightValidationError) {
      sources.push('Lightweight Code Validation');
    }

    if (this.hasResult) {
      sources.push('Source Code Assessment');
    }

    if (this.application?.tcoCost != null) {
      sources.push('Cost Signals');
    }

    if (this.application?.users != null || this.application?.incidents != null) {
      sources.push('Operational Signals');
    }

    return sources.join(', ');
  }

  get assessmentCoveragePercent(): number {
    const expectedSources = 5;
    let availableSources = 1; // CMDB metadata is always present on this screen.

    if (this.hasLightweightValidationResult || this.hasLightweightValidationError) {
      availableSources++;
    }

    if (this.hasResult) {
      availableSources++;
    }

    if (this.application?.tcoCost != null) {
      availableSources++;
    }

    if (this.application?.users != null || this.application?.incidents != null) {
      availableSources++;
    }

    return Math.round((availableSources / expectedSources) * 100);
  }

  get isLowAssessmentCoverage(): boolean {
    return this.assessmentCoveragePercent < this.assessmentCoverageThresholdPercent;
  }

  get assessmentCoverageRecommendation(): string {
    if (!this.isLowAssessmentCoverage) {
      return '';
    }

    if (this.isRepositoryUnavailableForAssessment) {
      return 'Recommended next step: Validate CMDB metadata and add repository access (Orion manifest) so code-based assessment can be performed.';
    }

    if (!this.hasLightweightValidationResult && !this.hasLightweightValidationError) {
      return 'Recommended next step: Run lightweight code validation to increase assessment coverage.';
    }

    if (!this.hasResult) {
      return 'Recommended next step: Run Source Code Assessment to increase assessment coverage.';
    }

    return 'Recommended next step: Add cost and operational signals for this application to improve assessment coverage.';
  }

  get assessmentCoverageThresholdPercent(): number {
    const configuredThreshold = this.configService.getConfig()?.assessment?.coverageThresholdPercent;
    if (typeof configuredThreshold !== 'number' || !Number.isFinite(configuredThreshold)) {
      return AssessmentDetailComponent.DEFAULT_ASSESSMENT_COVERAGE_THRESHOLD_PERCENT;
    }

    const roundedThreshold = Math.round(configuredThreshold);
    if (roundedThreshold < 1 || roundedThreshold > 100) {
      return AssessmentDetailComponent.DEFAULT_ASSESSMENT_COVERAGE_THRESHOLD_PERCENT;
    }

    return roundedThreshold;
  }

  get showCmdbCodeDifferenceMessage(): boolean {
    if (!this.application || !this.hasResult || this.isFailed) {
      return false;
    }

    const cmdbResult = this.normalizeAssessmentCategory(this.application.outcome);
    const codeResult = this.normalizeAssessmentCategory(this.viewModel?.job?.result?.summary?.recommendation);

    if (!cmdbResult || !codeResult) {
      return false;
    }

    return cmdbResult !== codeResult;
  }

  get cmdbConfidenceLevel(): string {
    return this.application?.cmdbConfidenceLevel || 'Unknown';
  }

  get cmdbConfidenceScoreLabel(): string {
    return this.application?.cmdbConfidenceScore != null ? `${this.application.cmdbConfidenceScore}%` : 'Not available';
  }

  get hasCmdbConfidenceSignals(): boolean {
    return this.cmdbConfidenceSignals.length > 0;
  }

  get cmdbConfidenceClass(): string {
    return `confidence-${this.cmdbConfidenceLevel.toLowerCase()}`;
  }

  get isLowCmdbConfidence(): boolean {
    return this.cmdbConfidenceLevel.toLowerCase() === 'low';
  }

  get cmdbOutcomeLabel(): string {
    return this.isLowCmdbConfidence ? 'Preliminary outcome' : 'Analysis Result';
  }

  get hasCmdbAnalysisDate(): boolean {
    return !!this.cmdbAnalyzedAt;
  }

  get cmdbAnalysisLastRunSentence(): string {
    if (!this.cmdbAnalyzedAt) {
      return 'This analysis has not been run yet.';
    }

    const parsed = new Date(this.cmdbAnalyzedAt);
    if (Number.isNaN(parsed.getTime())) {
      return 'This analysis has not been run yet.';
    }

    return `Last run on ${formatUtcDate(parsed, 'short')} UTC.`;
  }

  get preliminaryCmdbOutcome(): string {
    return this.application?.preliminaryOutcome || this.application?.outcome || '-';
  }

  get isCmdbOnlyRecommendation(): boolean {
    const basis = this.application?.assessmentBasis ?? '';
    const normalized = basis.toLowerCase();
    return normalized.includes('cmdb') && normalized.includes('only');
  }

  get cmdbOnlyDisclaimerText(): string {
    return AssessmentDetailComponent.CMDB_ONLY_DISCLAIMER_MESSAGE;
  }

  get cmdbPreliminaryText(): string {
    return AssessmentDetailComponent.CMDB_PRELIMINARY_MESSAGE;
  }

  get hasAssessmentSummaryNotice(): boolean {
    return this.hasLightweightValidationResult
      || this.hasLightweightValidationError
      || this.isRepositoryUnavailableForAssessment
      || this.isCmdbOnlyRecommendation
      || this.showCmdbCodeDifferenceMessage
      || this.isLowAssessmentCoverage;
  }

  get showLightweightSummary(): boolean {
    return !this.hasCmdbRetirementDirective
      && (this.lightweightValidations.length > 0 || this.isRepositoryUnavailableForAssessment);
  }

  get hasMultipleValidatedRepositories(): boolean {
    return this.lightweightValidations.length > 1;
  }

  /** Highest legacy-risk repository finding used for the business-friendly summary. */
  get primaryLightweightFinding(): LightweightValidationView | null {
    return this.lightweightValidations[0] ?? null;
  }

  get lightweightAnalysisResultLabel(): string {
    const primary = this.primaryLightweightFinding;
    if (!primary) {
      return '-';
    }
    return primary.analysisResultLabel;
  }

  get lightweightKeyReasonLabel(): string {
    return this.primaryLightweightFinding?.message || '-';
  }

  get lightweightRecommendationLabel(): string {
    return this.primaryLightweightFinding?.recommendedAction || '-';
  }

  get lightweightConfidenceLabel(): string {
    return this.primaryLightweightFinding?.confidence || 'Not available';
  }

  get lightweightConfidenceClass(): string {
    const confidence = (this.primaryLightweightFinding?.confidence || '').toLowerCase();
    return confidence ? `confidence-${confidence}` : 'confidence-unknown';
  }

  get lightweightRunDateLabel(): string | null {
    return this.primaryLightweightFinding?.runDate ?? null;
  }

  get lightweightRationaleLabel(): string {
    const primary = this.primaryLightweightFinding;
    if (!primary) {
      return 'Lightweight code assessment rationale not available.';
    }
    return primary.scoreExplanation || primary.message || 'Lightweight code assessment rationale not available.';
  }

  get lightweightRepositoryFilterOptions(): LightweightValidationView[] {
    return this.lightweightValidations;
  }

  /** Repositories shown in the details grid, honoring the repository select-box filter. */
  get filteredLightweightValidations(): LightweightValidationView[] {
    if (this.selectedLightweightRepositoryJobId === 'all') {
      return this.lightweightValidations;
    }
    return this.lightweightValidations.filter(v => v.jobId === this.selectedLightweightRepositoryJobId);
  }

  onLightweightRepositoryFilterChange(value: string): void {
    this.selectedLightweightRepositoryJobId = value === 'all' ? 'all' : Number(value);
  }

  toggleRepositoryDetailsSection(): void {
    this.isRepositoryDetailsExpanded = !this.isRepositoryDetailsExpanded;
  }

  isLightweightDetailsExpanded(jobId: number): boolean {
    return this.expandedLightweightValidationJobIds.has(jobId);
  }

  toggleLightweightDetails(jobId: number): void {
    if (this.expandedLightweightValidationJobIds.has(jobId)) {
      this.expandedLightweightValidationJobIds.delete(jobId);
    } else {
      this.expandedLightweightValidationJobIds.add(jobId);
    }
  }

  get hasLightweightConflict(): boolean {
    return !this.hasCmdbRetirementDirective && this.lightweightValidations.some(v => v.isConflict);
  }

  get canRunLightweightValidation(): boolean {
    return !this.hasCmdbRetirementDirective;
  }

  get runLightweightValidationDisabledReason(): string {
    return this.hasCmdbRetirementDirective
      ? 'Lightweight validation is not applicable when CMDB contains an explicit end-of-life, retirement, or decommission directive.'
      : '';
  }

  get repositoryUnavailableMessage(): string {
    return AssessmentDetailComponent.LIGHTWEIGHT_UNABLE_TO_VALIDATE_MESSAGE;
  }

  /**
   * Returns true when the "Run Code Assessment" button should be enabled.
   * Conditions (at least one must be met):
   *  - AI-Powered CMDB Analysis outcome is "Modernize"
   *  - Disposition has been manually overridden to "Modernize"
   */
  get canRunCodeAssessment(): boolean {
    const outcome = (this.application?.outcome ?? '').toLowerCase();
    const override = (this.application?.overrideAction ?? '').toLowerCase();
    return outcome.includes('modernize') || override === 'modernize';
  }

  get runCodeAssessmentDisabledReason(): string {
    if (this.isEnterpriseLead && !this.isProjectLead) {
      return 'Only Project Leads can run code assessment.';
    }

    if (this.isActionProcessing) {
      return 'Another action is currently processing. Please wait.';
    }

    if (!this.canRunCodeAssessment) {
      return 'Code Assessment is available only when CMDB Analysis result is Modernize, or disposition has been manually overridden to Modernize.';
    }

    return '';
  }

  get businessCriticalityDisplay(): string {
    const app = this.application;
    if (!app) return '-';
    if (app.businessCriticality) return app.businessCriticality;

    // Match application grid fallback: derive max tier from A/C/I values.
    const vals = [app.availability, app.confidentiality, app.integrity]
      .map((v: string | null) => (v || '').replace(/\s+/g, '').toLowerCase());
    if (vals.includes('tier3')) return 'Tier3';
    if (vals.includes('tier2')) return 'Tier2';
    if (vals.includes('tier1')) return 'Tier1';
    return '-';
  }

  get selectedJob(): AssessmentJob | undefined {
    return this.allJobs.find(j => j.id === this.selectedJobId);
  }

  get rateLimitedJobs(): AssessmentJob[] {
    return this.allJobs.filter(job => {
      const status = String(job.status).toLowerCase();
      const pending = status === 'pending' || status === '0';
      const active = pending || status === 'running' || status === '1';
      return active && (!!job.rateLimitedUntil || (pending && (job.rateLimitRetryCount ?? 0) > 0));
    });
  }

  get isAnyJobRateLimited(): boolean {
    return this.rateLimitedJobs.length > 0;
  }

  getRateLimitMessageFor(job: AssessmentJob): string {
    const requester = job.requestedByEmail ? `, requested by ${job.requestedByEmail}` : '';
    const context = `Assessment #${job.id} for ${this.buildRepositoryLabel(job.repositoryUrl)}${requester}.`;
    const retryAt = job.rateLimitedUntil ? new Date(job.rateLimitedUntil) : null;
    if (retryAt && Number.isFinite(retryAt.getTime())) {
      return retryAt.getTime() > Date.now()
        ? `${context} Expected retry after ${formatUtcDate(retryAt, 'short')} UTC.`
        : `${context} Awaiting automatic retry; the GitHub wait period has elapsed.`;
    }
    return `${context} An automatic retry is queued; its time is not yet available.`;
  }

  get hasLightweightValidationResult(): boolean {
    return !this.hasCmdbRetirementDirective && this.lightweightValidations.some(v => v.hasResult);
  }

  get hasLightweightValidationError(): boolean {
    return !this.hasCmdbRetirementDirective && this.lightweightValidations.some(v => v.isUnavailable);
  }

  private get hasCmdbRetirementDirective(): boolean {
    const app = this.application;
    return [
      app?.plannedDisposition,
      app?.lifeCycleStageStatus,
      app?.outcome,
      app?.preliminaryOutcome,
      app?.aiKeyReason
    ].some(value => /retire|decommission|sunset|phase[\s-]+out|end[\s-]+of[\s-]+life|\beol\b/i.test(value ?? ''));
  }

  get lightweightValidationErrorMessage(): string {
    return AssessmentDetailComponent.LIGHTWEIGHT_UNABLE_TO_VALIDATE_MESSAGE;
  }

  get isRepositoryUnavailableForAssessment(): boolean {
    if (!this.application?.orionManifestJson?.trim()) {
      return true;
    }

    try {
      const manifest = JSON.parse(this.application.orionManifestJson);
      const repositories = manifest?.application?.repositories;
      return !Array.isArray(repositories) || repositories.length === 0;
    } catch {
      return true;
    }
  }

  get historyNote(): string | null {
    if (this.isHistoryLoading || this.applicationHistory.length === 0) return null;
    var latestUpdate: string | null = null;
    for (var history of this.applicationHistory.reverse()) {
      if (history.action == 'Approved') {
        latestUpdate = `AI-Powered CMDB disposition has been reviewed and approved by the ${history.assignedByRole} (${history.createdBy})`;
      }
      else if (history.action == 'Rejected') {
        latestUpdate = `This App has been rejected by the ${history.assignedByRole} (${history.createdBy}). Rejection reason: ${history.rejectionReason}. ${history.rejectionDetail ? `Rejection detail: ${history.rejectionDetail}` : ''}`;
      }
      else if (history.action !== history.outcome && history.action !== 'Assigned') {
        latestUpdate = `AI-Powered CMDB disposition has been manually overridden to ${history.action} by the ${history.assignedByRole} (${history.createdBy})`;
      }
      else if (history.action == 'Assigned') {
        const approvalRecord = this.applicationHistory.find(appHistory => appHistory.action == 'Approved');
        const approvedBy = approvalRecord?.createdBy;
        const isAssignedToAMORETeam = history.assignedTo == 'Engine-IT-Ops-AppRat-Employees@chevron.com';
        const assignmentPart = isAssignedToAMORETeam ? `Currently, this App has been assigned to the AMORE Team for implementation.` : `Currently, this App has been assigned to ${history.assignedTo} by the ${history.assignedByRole}.`;
        const approvalPart = approvalRecord
          ? `AI-Powered CMDB disposition has been reviewed and approved by ${approvalRecord.assignedByRole} (${approvedBy}). `
          : '';
        latestUpdate = approvalPart + assignmentPart;
      }
    }
    if (!latestUpdate) return null;
    return 'Note: ' + latestUpdate;
  }

  get totalScore(): number | null {
    const score = this.viewModel?.job?.result?.totalScore;
    if (score == null) return null;
    // Clamp to 0-100 range for safety
    return Math.max(0, Math.min(100, score));
  }

  private formatLightweightValue(value?: string | null): string {
    if (!value) {
      return '';
    }

    return value
      .replace(/[_-]+/g, ' ')
      .replace(/([a-z])([A-Z])/g, '$1 $2')
      .replace(/\s+/g, ' ')
      .trim();
  }

  get scoreColorClass(): string {
    const score = this.totalScore;
    if (score == null) return '';
    if (score < 20) return 'score-very-low';
    if (score < 46) return 'score-low';
    if (score < 70) return 'score-medium';
    return 'score-high';
  }

  private buildDimensionItems(): void {
    if (!this.viewModel?.job?.result?.dimensions) {
      this.dimensionItems = [];
      return;
    }

    const dims = this.viewModel.job.result.dimensions;
    this.dimensionItems = [
      { key: 'cicd', ...this.withReasoningViewData(dims.cicd.reasoning, dims.cicd) },
      { key: 'auth', ...this.withReasoningViewData(dims.auth.reasoning, dims.auth) },
      { key: 'code', ...this.withReasoningViewData(dims.code.reasoning, dims.code) },
      { key: 'dependencies', ...this.withReasoningViewData(dims.dependencies.reasoning, dims.dependencies) },
      { key: 'hosting', ...this.withReasoningViewData(dims.hosting.reasoning, dims.hosting) },
      { key: 'api', ...this.withReasoningViewData(dims.api.reasoning, dims.api) },
      { key: 'container', ...this.withReasoningViewData(dims.container.reasoning, dims.container) },
      { key: 'testing', ...this.withReasoningViewData(dims.testing.reasoning, dims.testing) },
      { key: 'production', ...this.withReasoningViewData(dims.production.reasoning, dims.production) },
      // Database dimension disabled — excluded from display and scoring
      // ...(dims.database ? [{ key: 'database', ...dims.database }] : []),
    ];
  }

  private withReasoningViewData<T extends { dimension: string; size: string; score: number; reasoning: string; actionItems?: string[] }>(
    reasoning: string,
    base: T
  ): T & { reasoningBullets: string[]; isReasoningBulletList: boolean; actionItems: string[] } {
    const parsed = this.parseReasoningBullets(reasoning);
    return {
      ...base,
      reasoningBullets: parsed.bullets,
      isReasoningBulletList: parsed.isBulletList,
      actionItems: (base.actionItems ?? []).filter(item => !!item && item.trim().length > 0),
    };
  }

  private parseReasoningBullets(reasoning: string): { bullets: string[]; isBulletList: boolean } {
    const normalized = (reasoning || '').replace(/\r\n/g, '\n').trim();
    if (!normalized) {
      return { bullets: ['-'], isBulletList: false };
    }

    const lines = normalized
      .split('\n')
      .map(line => line.trim())
      .filter(line => line.length > 0);

    const bulletPattern = /^[-*•]\s+(.+)$/;
    const numberedPattern = /^\d+\.\s+(.+)$/;

    const parsedLines = lines
      .map(line => {
        const bulletMatch = line.match(bulletPattern);
        if (bulletMatch) return bulletMatch[1].trim();

        const numberedMatch = line.match(numberedPattern);
        if (numberedMatch) return numberedMatch[1].trim();

        return null;
      })
      .filter((line): line is string => !!line && line.length > 0);

    if (parsedLines.length > 0 && parsedLines.length === lines.length) {
      return { bullets: parsedLines, isBulletList: true };
    }

    return { bullets: [normalized], isBulletList: false };
  }

  private normalizeAssessmentCategory(value: string | null | undefined): string {
    const normalized = (value || '').trim().toLowerCase();
    if (!normalized) {
      return '';
    }

    if (normalized.includes('excluded') || normalized.includes('out of scope') || normalized.includes('decommission')) {
      return 'excluded';
    }

    if (normalized.includes('already modern') || normalized.includes('no action') || normalized.includes('retain')) {
      return 'already modern';
    }

    if (
      normalized.includes('modernize') ||
      normalized.includes('refactor') ||
      normalized.includes('rewrite') ||
      normalized.includes('replatform') ||
      normalized.includes('rehost')
    ) {
      return 'modernize';
    }

    return normalized;
  }

  private isLightweightJob(job: AssessmentJob | null | undefined): boolean {
    return job?.jobType === AssessmentJobType.LightweightCodeValidation
      || job?.jobType === 'LightweightCodeValidation'
      || job?.jobType === '1'
      || (job?.jobType as any) === 1;
  }

  private isFullCodeAssessmentJob(job: AssessmentJob | null | undefined): boolean {
    return !this.isLightweightJob(job);
  }

  private isFinishedJob(job: AssessmentJob): boolean {
    return job.status === 'Completed' || job.status === 'Failed'
      || (job.status as any) === 2 || (job.status as any) === 3
      || job.status === '2' || job.status === '3';
  }

  constructor(
    private readonly route: ActivatedRoute,
    private readonly router: Router,
    private readonly reviewService: ModernizationReviewService,
    private readonly assessmentService: ModernizationAssessmentService,
    private readonly dialog: MatDialog,
    private readonly userContext: UserContextService,
    private readonly dashboardService: DashboardService,
    private readonly aiAssessmentService: AiAssessmentService,
    private readonly ngZone: NgZone,
    private readonly cdr: ChangeDetectorRef,
    private readonly assignmentService: AssignmentService,
    private readonly loggedUserService: LoggedUserService,
    private readonly applicationsStateService: ApplicationsStateService,
    private readonly configService: ConfigService,
  ) { }

  ngOnInit(): void {
    // Initialize persona actions
    this.personaActions = Object.values(PERSONA_ACTIONS).map(value => ({ personaActionStatusValue: value }));

    // Load rejection reasons
    this.assignmentService.getRejectionReasons().subscribe((reasons) => {
      this.rejectionReasons = reasons;
    });

    // Read source query param
    this.sourcedFromDashboard = this.route.snapshot.queryParamMap.get('source') === 'dashboard';

    // Check user roles for workflow
    this.userContext.userRoleSubject
      .pipe(takeUntil(this.destroy$))
      .subscribe((roles) => {
        this.isAMORETeam = roles.includes(Role.AMORETeam);
        this.isEnterpriseLead = roles.includes(Role.EnterpriseLead);
        this.isProjectLead = roles.includes(Role.ProjectLead);
        this.isArchitect = this.isEnterpriseLead || this.isProjectLead;
      });

    // Get logged user email
    this.loggedUserService.getLoggedUserData()
      .pipe(takeUntil(this.destroy$))
      .subscribe((loggedUserData) => {
        if (loggedUserData !== null) {
          this.loggedUserEmail = loggedUserData.email;
        }
      });

    this.route.paramMap
      .pipe(
        takeUntil(this.destroy$),
        switchMap((params) => {
          const idParam = params.get("id");
          this.trackedBusinessApplicationId = idParam ? parseInt(idParam, 10) : undefined;
          this.id = this.trackedBusinessApplicationId;
          this.applicationHistory = [];
          this.isHistoryLoading = true;
          if (!this.trackedBusinessApplicationId) {
            return of(null);
          }
          return this.loadAssessment(this.trackedBusinessApplicationId);
        })
      )
      .subscribe({
        next: (vm) => {
          if (vm) {
            this.viewModel = vm;
            // Set sysId from application for review operations
            this.sysId = vm.application.sysId;
            // Set isAiGenerated and finalization status
            this.isAiGenerated = (vm.job as AssessmentJob | null)?.result != null;
            this.buildScoreTiles();
            this.renderRadarChart();
            // Load review status if we have a sysId
            if (this.sysId) {
              this.loadReviewStatus();
            }
            // Load assignment history
            this.loadApplicationHistory(vm.application.appId);
          }
        },
        error: (err) => {
          console.error("Error loading assessment:", err);
          this.loadError = "Failed to load assessment data.";
          this.isLoading = false;
        },
      });
  }

  ngAfterViewInit(): void {
    this.viewReady = true;
    // Defer to ensure layout has a non-zero canvas size.
    setTimeout(() => this.renderRadarChart());
  }

  ngOnDestroy(): void {
    this.stopJobPolling();
    this.stopCmdbAnalysisRefreshPolling();
    this.radarChart?.destroy();
    this.destroy$.next();
    this.destroy$.complete();
  }

  private startJobPolling(): void {
    if (this.jobPollingTimer) return;
    this.jobPollingTimer = setInterval(() => {
      if (!this.trackedBusinessApplicationId) return;
      this.aiAssessmentService.getJobHistoryByTrackedBusinessApplicationId(this.trackedBusinessApplicationId, false)
        .pipe(catchError(() => of(null)), takeUntil(this.destroy$))
        .subscribe(jobs => {
          if (!jobs) return;
          this.ngZone.run(() => {
            const stillRunning = jobs.some(j =>
              j.status === 'Pending' || j.status === 'Running' ||
              (j.status as any) === 0 || (j.status as any) === 1 ||
              j.status === '0' || j.status === '1'
            );
            this.allJobs = jobs;
            this.hasRunningJob = stillRunning;
            if (!stillRunning) {
              this.hasRunningJob = false;
              this.stopJobPolling();
              // Notify user if a newer completed or failed result exists beyond what's displayed
              const latestFinished = jobs.find(j => this.isFinishedJob(j));
              if (latestFinished && latestFinished.id !== this.selectedJobId) {
                if (this.isLightweightJob(latestFinished)) {
                  this.loadLightweightValidations(jobs);
                } else if (this.selectedJobId === null) {
                  // No prior assessment was displayed — auto-load the new result
                  this.onSelectHistoryJob(latestFinished);
                } else {
                  // User is viewing an older assessment — show the banner
                  this.hasNewerAssessment = true;
                }
              }
            }
            this.cdr.detectChanges();
          });
        });
    }, 15000);
  }

  private stopJobPolling(): void {
    if (this.jobPollingTimer) {
      clearInterval(this.jobPollingTimer);
      this.jobPollingTimer = null;
    }
  }

  backToApplicationPortfolio(): void {
    if (this.sourcedFromDashboard) {
      this.router.navigate(['/dashboard']);
      return;
    }
    const savedState = this.applicationsStateService.restore();
    if (savedState?.assessmentstatus && savedState?.assessmentstatus.length > 0) {
      this.router.navigate(["/applications"], { queryParams: { assessmentStatus: savedState.assessmentstatus } });
    }
    else if (savedState?.strategyStatus && savedState?.strategyStatus.length > 0) {
      this.router.navigate(["/applications"], { queryParams: { strategyStatus: savedState.strategyStatus } });
    }
    else if ((savedState?.type && savedState?.type.length > 0) && (savedState?.priority && savedState?.priority.length > 0)) {
      this.router.navigate(["/applications"], { queryParams: { type: savedState.type, priority: savedState.priority } });
    }
    else {
      this.router.navigate(["/applications"]);
    }
  }

  goToComplexitySizing(): void {
    if (this.trackedBusinessApplicationId) {
      this.router.navigate(["/complexity-sizing", this.trackedBusinessApplicationId]);
    }
  }

  goToReportGenerator(): void {
    if (this.trackedBusinessApplicationId) {
      this.router.navigate(["/report", this.trackedBusinessApplicationId]);
    }
  }

  private loadAssessment(trackedBusinessApplicationId: number) {
    this.isLoading = true;
    this.isJobLoading = true;
    this.loadError = undefined;
    this.cmdbConfidenceSignals = [];

    // Step 1: Load application data and job metadata (without large result JSON)
    return this.dashboardService.getApplicationByTrackedBusinessApplicationId(trackedBusinessApplicationId).pipe(
      catchError(() => of(null)),
      switchMap((application) => {
        if (!application) {
          this.loadError = `Application with TrackedBusinessApplicationId "${trackedBusinessApplicationId}" not found.`;
          this.isLoading = false;
          return of(null);
        }

        this.isLoading = false;

        // Return application with initial viewModel
        const initialViewModel = { application, job: null, hasResult: false };

        if (this.viewModel) {
          this.viewModel = { ...this.viewModel, application };
        }

        // Step 2: Load job list WITHOUT results (fast, lightweight)
        setTimeout(() => {
          this.aiAssessmentService.getJobHistoryByTrackedBusinessApplicationId(trackedBusinessApplicationId, false)
            .pipe(catchError(() => of(null)), takeUntil(this.destroy$))
            .subscribe({
              next: (jobs) => {
                if (!jobs) {
                  this.isJobLoading = false;
                  this.startJobPolling();
                  this.cdr.detectChanges();
                  return;
                }
                // Detect if any job is currently Pending or Running
                const runningJob = jobs.find(j =>
                  j.status === 'Pending' || j.status === 'Running' ||
                  (j.status as any) === 0 || (j.status as any) === 1 ||
                  j.status === '0' || j.status === '1'
                );
                this.hasRunningJob = !!runningJob;
                this.allJobs = jobs;
                if (this.hasRunningJob) {
                  this.startJobPolling();
                } else {
                  this.stopJobPolling();
                }

                const latestLightweightJob = jobs.find(j => this.isLightweightJob(j) && this.isFinishedJob(j));
                if (latestLightweightJob) {
                  this.loadLightweightValidations(jobs);
                } else {
                  this.lightweightValidations = [];
                }

                // Find first completed or failed full code assessment (most recent finished assessment)
                let selectedJobId: number | null = null;
                for (const job of jobs) {
                  if (this.isFullCodeAssessmentJob(job) && this.isFinishedJob(job)) {
                    selectedJobId = job.id;
                    break;
                  }
                }

                // Fallback to first full code job if no completed full assessment exists.
                if (!selectedJobId) {
                  selectedJobId = jobs.find(j => this.isFullCodeAssessmentJob(j))?.id ?? null;
                }

                this.selectedJobId = selectedJobId;

                // Step 3: If we have a job, load its full details with result
                if (selectedJobId && this.viewModel) {
                  this.aiAssessmentService.getJob(selectedJobId).subscribe({
                    next: (jobWithResult) => {
                      // Run updates outside Angular zone to prevent immediate change detection
                      this.ngZone.runOutsideAngular(() => {
                        const hasResult = !!(jobWithResult?.result);
                        this.viewModel = { ...this.viewModel!, job: jobWithResult, hasResult };
                        this.buildScoreTiles();
                        this.buildDimensionItems();

                        // Trigger change detection manually in next tick
                        setTimeout(() => {
                          this.ngZone.run(() => {
                            this.isJobLoading = false;
                            this.cdr.detectChanges();

                            // Render chart after DOM update
                            setTimeout(() => {
                              this.renderRadarChart();
                            }, 0);
                          });
                        }, 0);
                      });
                    },
                    error: (err) => {
                      console.error('[AssessmentDetail] Failed to load job details:', err);
                      this.isJobLoading = false;
                      this.cdr.detectChanges();
                    }
                  });
                } else {
                  // No job to load — mark as done immediately
                  this.isJobLoading = false;
                  this.cdr.detectChanges();
                }
              },
              error: (err) => {
                console.error("Failed to load job history:", err);
                this.isJobLoading = false;
                this.cdr.detectChanges();
              }
            });
        }, 0);

        return of(initialViewModel);
      })
    );
  }

  private buildScoreTiles(): void {
    if (!this.viewModel) return;

    const result = this.viewModel.job?.result;
    const app = this.viewModel.application;

    this.scoreTiles = [];

    if (result) {
      // Build tiles from ResultJson categoryScores + totalScore + MVI
      const cs = result.categoryScores;
      this.scoreTiles = [
        {
          label: "Operational Efficiency",
          value: cs.operationalEfficiencyScore,
          subtitle: "Automation & process",
          variant: "metric-tile--primary",
        },
        {
          label: "Technical Agility",
          value: cs.technicalAgilityScore,
          subtitle: "Development velocity",
          variant: "metric-tile--success",
        },
        {
          label: "Cloud Optimization",
          value: cs.cloudOptimizationScore,
          subtitle: "Cloud readiness",
          variant: "metric-tile--info",
        },
        {
          label: "Security Compliance",
          value: cs.securityComplianceScore,
          subtitle: "Risk & compliance",
          variant: "metric-tile--warning",
        },
        {
          label: "Total Score",
          value: result.totalScore,
          subtitle: "Overall assessment",
          variant: "metric-tile--primary",
        },
        {
          label: "Modernization Value Index",
          value: result.modernizationValueIndex,
          subtitle: "Business value",
          variant: "metric-tile--success",
        },
      ];
    } else {
      // Fallback: show basic metrics from portfolio if available
      if (app.readinessScore != null) {
        this.scoreTiles.push({
          label: "Readiness Score",
          value: app.readinessScore,
          subtitle: "CMDB-based readiness",
          variant: "metric-tile--primary",
        });
      }
      if (app.codeAnalysisScore != null) {
        this.scoreTiles.push({
          label: "Code Analysis Score",
          value: app.codeAnalysisScore,
          subtitle: "Code assessment",
          variant: "metric-tile--info",
        });
      }
    }

  }

  private loadLightweightValidations(jobs: AssessmentJob[]): void {
    // Jobs arrive newest-first, so the first hit per repository is its latest result.
    const latestPerRepository = new Map<string, AssessmentJob>();
    for (const job of jobs) {
      if (!this.isLightweightJob(job) || !this.isFinishedJob(job)) {
        continue;
      }
      const key = (job.repositoryUrl ?? '').toLowerCase();
      if (!latestPerRepository.has(key)) {
        latestPerRepository.set(key, job);
      }
    }

    const targets = Array.from(latestPerRepository.values())
      .slice(0, AssessmentDetailComponent.MAX_LIGHTWEIGHT_REPOSITORIES);

    if (targets.length === 0) {
      this.lightweightValidations = [];
      this.cdr.detectChanges();
      return;
    }

    forkJoin(targets.map(job => this.aiAssessmentService.getJob(job.id).pipe(catchError(() => of(null)))))
      .pipe(takeUntil(this.destroy$))
      .subscribe(loadedJobs => {
        this.lightweightValidations = loadedJobs
          .filter((job): job is AssessmentJob => !!job)
          .map(job => this.buildLightweightValidationView(job))
          .sort((a, b) => {
            if (a.score !== b.score) {
              if (a.score == null) {
                return 1;
              }
              if (b.score == null) {
                return -1;
              }
              return b.score - a.score;
            }

            const conflictOrder = Number(b.isConflict) - Number(a.isConflict);
            if (conflictOrder !== 0) {
              return conflictOrder;
            }

            const unavailableOrder = Number(b.isUnavailable) - Number(a.isUnavailable);
            return unavailableOrder !== 0
              ? unavailableOrder
              : a.repositoryLabel.localeCompare(b.repositoryLabel);
          });
        this.cdr.detectChanges();
      });
  }

  private buildLightweightValidationView(job: AssessmentJob): LightweightValidationView {
    const result = job.lightweightResult;
    const comparison = (result?.cmdbComparison ?? '').toLowerCase();
    const isFailed = job.status === 'Failed' || job.status === '3' || (job.status as any) === 3;
    const hasResult = !!result;
    const isUnavailable = (isFailed && !hasResult) || comparison.includes('unable');
    const isConflict = comparison.includes('conflict') || comparison.includes('mismatch');

    const uniqueSignals = Array.from(new Set((result?.legacyIndicators ?? []).map(i => i.signal).filter(Boolean)));
    const stack = result?.technologyStack ?? [];

    let message: string;
    let recommendedAction: string;
    if (isUnavailable) {
      message = AssessmentDetailComponent.LIGHTWEIGHT_UNABLE_TO_VALIDATE_MESSAGE;
      recommendedAction = 'Continue with the CMDB-based recommendation and verify repository access before rerunning lightweight validation.';
    } else if (isConflict) {
      message = result?.message || result?.summary || 'Lightweight code validation differs from the CMDB assessment.';
      recommendedAction = result?.recommendedAction || '-';
    } else {
      message = result?.summary || result?.message || 'Repository metadata was validated against CMDB signals.';
      recommendedAction = result?.recommendedAction || '-';
    }

    let outcomeClass = 'lightweight-outcome-ok';
    if (isUnavailable || isConflict) {
      outcomeClass = 'lightweight-outcome-conflict';
    } else if (comparison.includes('insufficient')) {
      outcomeClass = 'lightweight-outcome-warning';
    }

    // Business-friendly risk wording for the lightweight source-code check.
    let analysisResultLabel: string;
    if (isUnavailable) {
      analysisResultLabel = 'Unable to Determine';
    } else if (isConflict) {
      analysisResultLabel = 'Review Recommended';
    } else {
      analysisResultLabel = 'Low Risk';
    }

    return {
      jobId: job.id,
      repositoryUrl: job.repositoryUrl ?? '',
      repositoryLabel: this.buildRepositoryLabel(job.repositoryUrl),
      branch: job.branch?.trim() || '-',
      hasResult,
      isConflict,
      isUnavailable,
      statusLabel: isUnavailable ? 'Unable to Validate' : (this.formatLightweightValue(result?.cmdbComparison) || 'Not run'),
      outcomeClass,
      message,
      recommendedAction,
      pipelineMaturity: this.formatLightweightValue(result?.pipelineMaturity) || '-',
      technologySignals: stack.length > 0 ? stack.join(', ') : '-',
      legacyIndicatorsPreview: uniqueSignals.slice(0, 3).join(', '),
      score: result?.score ?? null,
      signals: result?.signals ?? [],
      scoreExplanation: result?.scoreExplanation?.trim() || '',
      confidence: result?.confidence?.trim() || '',
      runDate: job.completedAt || job.createdAt || null,
      analysisResultLabel
    };
  }

  private buildRepositoryLabel(repositoryUrl?: string | null): string {
    const url = (repositoryUrl ?? '').trim().replace(/\.git$/i, '');
    if (!url) {
      return 'Repository';
    }

    const parts = url.split('/').filter(part => part.length > 0);
    return parts.length >= 2 ? `${parts[parts.length - 2]}/${parts[parts.length - 1]}` : url;
  }

  private renderRadarChart(): void {
    if (!this.viewReady || !this.viewModel || !this.radarCanvas) {

      return;
    }

    this.radarChart?.destroy();

    const result = this.viewModel.job?.result;
    if (!result?.dimensions) {
      return; // Don't render chart if no dimensions
    }

    const rootStyle = getComputedStyle(document.documentElement);
    const primaryRgb = rootStyle.getPropertyValue("--bs-primary-rgb").trim() || "0,123,255";

    const mapSizeToValue = (size: string | null | undefined): number => {
      const normalized = (size ?? "").trim().toLowerCase();
      if (normalized === "small") return 1;
      if (normalized === "medium") return 2;
      if (normalized === "large") return 3;
      // Includes "n/a" and any unknown value
      return 0;
    };

    // Build labels and data from dimensions (N/A=0, Small=1, Medium=2, Large=3)
    const items = this.dimensionItems;
    if (items.length === 0) {
      return;
    }
    const labels = items.map((i) => i.dimension);
    const data = items.map((i) => mapSizeToValue(i.size));

    this.radarChart = new Chart(this.radarCanvas.nativeElement, {
      type: "radar",
      data: {
        labels,
        datasets: [
          {
            label: "Scores",
            data,
            backgroundColor: `rgba(${primaryRgb}, 0.20)`,
            borderColor: `rgba(${primaryRgb}, 0.80)`,
            pointBackgroundColor: `rgba(${primaryRgb}, 0.90)`,
            pointRadius: 2,
            borderWidth: 2,
          },
        ],
      },
      options: {
        responsive: true,
        maintainAspectRatio: false,
        plugins: {
          legend: {
            display: false,
          },
          tooltip: {
            enabled: true,
          },
        },
        scales: {
          r: {
            beginAtZero: true,
            min: 0,
            max: 3,
            ticks: {
              display: false,
              stepSize: 1,
            },
            grid: {
              color: "rgba(0,0,0,0.08)",
            },
            angleLines: {
              color: "rgba(0,0,0,0.08)",
            },
            pointLabels: {
              color: "rgba(0,0,0,0.60)",
              font: {
                size: 11,
              },
            },
          },
        },
      },
    });

  }

  private loadApplicationHistory(appId: string): void {
    this.assignmentService.getApplicationHistory(appId).subscribe({
      next: (history) => {
        this.applicationHistory = history;
        this.isHistoryLoading = false;
      },
      error: (err) => {
        console.error('Failed to load application history:', err);
        this.isHistoryLoading = false;
      }
    });
  }

  private loadApprovalLogs(sysId: string): void {
    this.reviewService.getApprovalLogs(sysId).subscribe({
      next: (logs) => {
        this.approvalLogs = logs;
        this.updateFinalDecisionLabel();
      },
      error: (err) => {
        console.error("Failed to load approval logs:", err);
      },
    });
  }

  private updateFinalDecisionLabel(): void {
    if (!this.isFinalized || this.approvalLogs.length === 0) {
      this.finalDecisionLabel = undefined;
      return;
    }

    // Get the most recent approval action
    const latestLog = this.approvalLogs[this.approvalLogs.length - 1];
    this.finalDecisionLabel = latestLog.action === 'Accepted' ? 'Finalized' : 'Architect Approved';
  }

  private loadReviewStatus(): void {
    if (!this.sysId) return;
    // Prefer querying the modernization assessment by its ID (if available),
    // otherwise fall back to using the job result already present in the view model.
    const modernizationAssessmentId = this.viewModel?.application?.modernizationAssessmentId;

    if (modernizationAssessmentId) {
      this.assessmentService.getById(modernizationAssessmentId).subscribe({
        next: (data) => {
          this.isFinalized = !!data.isFinalized;
          this.finalizedBy = data.finalizedBy || undefined;
          this.finalizedAt = data.finalizedAt ? new Date(data.finalizedAt) : undefined;
          this.isAiGenerated = !!data.isAiGenerated;
          this.cmdbAnalyzedAt = data.aiAssessedAt ?? null;
          if (this.viewModel?.application) {
            this.viewModel.application.cmdbConfidenceScore = data.cmdbConfidence?.score ?? this.viewModel.application.cmdbConfidenceScore;
            this.viewModel.application.cmdbConfidenceLevel = data.cmdbConfidence?.level ?? this.viewModel.application.cmdbConfidenceLevel;
            this.viewModel.application.assessmentBasis = data.assessmentBasis ?? this.viewModel.application.assessmentBasis;
            this.viewModel.application.preliminaryOutcome = data.preliminaryOutcome ?? this.viewModel.application.preliminaryOutcome;
            this.viewModel.application.lightweightValidationStatus = data.lightweightValidationStatus ?? this.viewModel.application.lightweightValidationStatus;
            this.viewModel.application.lightweightValidationSummary = data.lightweightValidationSummary ?? this.viewModel.application.lightweightValidationSummary;
            this.viewModel.application.lightweightValidationRecommendation = data.lightweightValidationRecommendation ?? this.viewModel.application.lightweightValidationRecommendation;
            this.viewModel.application.lightweightValidatedAt = data.lightweightValidatedAt ?? this.viewModel.application.lightweightValidatedAt;
            this.cmdbConfidenceSignals = data.cmdbConfidence?.signals ?? [];
          }
          // Always load approval logs (use sysId which is the CMDB sys id)
          this.reviewService.getApprovalLogs(this.sysId!).subscribe({
            next: (logs) => {
              this.approvalLogs = logs;
              this.updateFinalDecisionLabel();
            },
            error: (err) => {
              console.error("Failed to load approval logs:", err);
            }
          });
        },
        error: (err) => {
          console.error("Failed to load review status:", err);
          // If the assessment lookup fails, still attempt to load approval logs and derive flags
          this.isAiGenerated = !!this.viewModel?.job?.result;
          this.reviewService.getApprovalLogs(this.sysId!).subscribe({
            next: (logs) => {
              this.approvalLogs = logs;
              this.updateFinalDecisionLabel();
            },
            error: (e) => console.error("Failed to load approval logs after assessment lookup failure:", e),
          });
        }
      });
    } else {
      // No modernizationAssessmentId available - derive state from the loaded job
      this.isAiGenerated = !!this.viewModel?.job?.result;
      this.isFinalized = false;
      this.cmdbAnalyzedAt = null;
      this.finalizedBy = undefined;
      this.finalizedAt = undefined;
      this.reviewService.getApprovalLogs(this.sysId!).subscribe({
        next: (logs) => {
          this.approvalLogs = logs;
          this.updateFinalDecisionLabel();
        },
        error: (err) => {
          console.error("Failed to load approval logs:", err);
        }
      });
    }
  }

  private openMessageDialog(title: string, message: string, link?: string): void {
    this.dialog.open(MessageDialogComponent, {
      width: '420px',
      data: { title, message, link } as MessageDialogData,
      disableClose: true
    });
  }

  openScoringLegend(): void {
    this.dialog.open(ScoringLegendDialogComponent, {
      width: '620px',
      maxHeight: '90vh'
    });
  }

  confirmCmdbAnalysisRefresh(): void {
    if (!this.sysId || this.isCmdbAnalysisRefreshing || this.isCmdbAnalysisRefreshQueued) {
      return;
    }

    const dialogRef = this.dialog.open(MessageDialogComponent, {
      width: '460px',
      disableClose: true,
      data: {
        title: 'Refresh CMDB Analysis?',
        message: `${this.cmdbAnalysisLastRunSentence} This will rerun the AI-powered CMDB analysis for this application and consume AI tokens. Do you want to continue?`,
        confirmMode: true,
        confirmLabel: 'Refresh Analysis',
        type: 'warning'
      } as MessageDialogData
    });

    dialogRef.afterClosed()
      .pipe(takeUntil(this.destroy$))
      .subscribe((confirmed) => {
        if (confirmed) {
          this.refreshCmdbAnalysis();
        }
      });
  }

  private refreshCmdbAnalysis(): void {
    if (!this.sysId) {
      return;
    }

    this.isCmdbAnalysisRefreshing = true;
    this.assessmentService.refreshCmdbAnalysis(this.sysId)
      .pipe(
        finalize(() => {
          this.isCmdbAnalysisRefreshing = false;
          this.cdr.detectChanges();
        }),
        takeUntil(this.destroy$)
      )
      .subscribe({
        next: (response) => {
          this.isCmdbAnalysisRefreshQueued = true;
          if (response.jobId != null) {
            this.startCmdbAnalysisRefreshPolling(response.jobId);
          }
        },
        error: (error) => {
          console.error('Failed to refresh CMDB analysis:', error);
          const message = error.error?.message
            || error.error?.detail
            || (typeof error.error === 'string' ? error.error : null)
            || 'The CMDB analysis could not be refreshed. Please try again.';
          this.openMessageDialog('Refresh Failed', message);
        }
      });
  }

  private startCmdbAnalysisRefreshPolling(jobId: number): void {
    this.stopCmdbAnalysisRefreshPolling();

    const pollJob = () => {
      this.assessmentService.getCmdbAnalysisRefreshJob(jobId)
        .pipe(takeUntil(this.destroy$))
        .subscribe({
          next: (job) => {
            const status = String(job.status).toLowerCase();
            if (status === 'completed' || status === '2') {
              this.stopCmdbAnalysisRefreshPolling();
              window.location.reload();
              return;
            }

            if (status === 'failed' || status === 'cancelled' || status === '3' || status === '4') {
              this.stopCmdbAnalysisRefreshPolling();
              this.isCmdbAnalysisRefreshQueued = false;
              this.openMessageDialog(
                'Refresh Failed',
                job.errorMessage || 'The CMDB analysis refresh did not complete successfully. Please try again.'
              );
              this.cdr.detectChanges();
            }
          },
          error: (error) => console.error('Failed to check CMDB analysis refresh status:', error)
        });
    };

    pollJob();
    this.cmdbRefreshPollingTimer = setInterval(pollJob, 5000);
  }

  private stopCmdbAnalysisRefreshPolling(): void {
    if (this.cmdbRefreshPollingTimer) {
      clearInterval(this.cmdbRefreshPollingTimer);
      this.cmdbRefreshPollingTimer = null;
    }
  }

  private proceedWithAssignment(result: { assignTo: string; email: string; selectedPodId?: number | null; notes: string }): void {
    if (!this.application) {
      return;
    }

    if (result.selectedPodId) {
      const assignPodPayload: AssignPodRequest = {
        appId: this.application.appId,
        podId: result.selectedPodId,
        notes: result.notes,
        trackedBusinessApplicationId: this.application.trackedBusinessApplicationId,
        assignedTo: result.assignTo,
        consentTimestamp: new Date(),
      };
      this.assignmentService.assignPod(assignPodPayload).subscribe({
        next: () => {
          if (this.trackedBusinessApplicationId) {
            this.loadAssessment(this.trackedBusinessApplicationId).subscribe();
            this.loadApplicationHistory(this.application!.appId);
          }
          this.openMessageDialog('Success', `The application has been assigned to POD #${result.selectedPodId} successfully.`);
        },
        error: (error) => {
          console.error("Error assigning POD:", error);
          this.openMessageDialog('Error', 'An error occurred. Please try again.');
        }
      });
      return;
    }

    var isOperationsManager = this.userContext.userRoleSubject.value.includes(Role.OperationsManager)
      || this.userContext.userRoleSubject.value.includes(Role.EnterpriseLead)
      || this.userContext.userRoleSubject.value.includes(Role.ProjectLead);

    var isAmoreTeam = this.userContext.userRoleSubject.value.includes(Role.AMORETeam);
    var assignment: Assignment;
    if (isAmoreTeam) {
      // This means assignment to someone within AMORE team
      assignment = {
        appId: this.application.appId,
        trackedBusinessApplicationId: this.application.trackedBusinessApplicationId,
        assignedTo: "",
        assignedByOperationManagerEmail: isOperationsManager ? (this.loggedUserEmail ?? null) : null,
        assignedRole: result.assignTo,
        executionAssignee: this.userContext.userRoleSubject.value.includes(Role.AMORETeam) ? result.email : null,
        notes: result.notes,
        consentTimestamp: isOperationsManager ? new Date() : null,
      };
    }
    else {
      assignment = {
        appId: this.application.appId,
        trackedBusinessApplicationId: this.application.trackedBusinessApplicationId,
        assignedTo: result.email,
        assignedByOperationManagerEmail: isOperationsManager ? (this.loggedUserEmail ?? null) : null,
        assignedRole: result.assignTo,
        executionAssignee: null,
        notes: result.notes,
        consentTimestamp: isOperationsManager ? new Date() : null,
      };
    }

    this.startActionProcessing('Creating ADO feature and generating PDF...');
    this.assignmentService.createAssignment(assignment)
      .pipe(finalize(() => this.stopActionProcessing()))
      .subscribe({
      next: () => {
        // Reload the assessment data
        if (this.trackedBusinessApplicationId) {
          this.loadAssessment(this.trackedBusinessApplicationId).subscribe();
          this.loadApplicationHistory(this.application!.appId);
        }
        this.openMessageDialog('Success', `The application has been assigned to ${result.assignTo}${result.email ? ` (${result.email})` : ''} successfully.`);
      },
      error: (error) => {
        console.error("Error creating assignment:", error);
        this.openMessageDialog('Error', 'An error occurred. Please try again.');
      }
    });
  }

  private startActionProcessing(loadingText: string): void {
    this.actionProcessingText = loadingText;
    this.isActionProcessing = true;

    if (!this.adoProcessingDialogRef) {
      this.adoProcessingDialogRef = this.dialog.open(AdoProcessingDialogComponent, {
        width: '460px',
        disableClose: true,
        autoFocus: false,
        data: {
          title: 'Creating ADO Feature',
          message: loadingText
        }
      });
    }
  }

  private stopActionProcessing(): void {
    this.isActionProcessing = false;
    if (this.adoProcessingDialogRef) {
      this.adoProcessingDialogRef.close();
      this.adoProcessingDialogRef = null;
    }
  }

  private proceedWithRejection(result: { rejectionReasonId: number; rejectionReason: string; otherReasonText: string }): void {
    if (!this.application) {
      return;
    }

    const rejectionData: Rejection = {
      appId: this.application.appId,
      trackedBusinessApplicationId: this.application.trackedBusinessApplicationId,
      assessmentStatusId: null,
      modernizationStatusId: null,
      personaActionStatusValue: null,
      rejectionReasonId: result.rejectionReasonId,
      rejectionDetail: result.otherReasonText.length > 0 ? result.otherReasonText : '',
      rejectionTimestamp: new Date(),
    };

    this.assignmentService.rejectApplication(rejectionData).subscribe({
      next: () => {
        // Reload the assessment data
        if (this.trackedBusinessApplicationId) {
          this.loadAssessment(this.trackedBusinessApplicationId).subscribe();
          this.loadApplicationHistory(this.application!.appId);
        }
        this.openMessageDialog('Success', 'The application has been rejected successfully.');
      },
      error: (error) => {
        console.error("Error rejecting application:", error);
        this.openMessageDialog('Error', 'An error occurred. Please try again.');
      }
    });
  }

  onDownloadPdfReport(): void {
    if (!this.id) return;
    this.isDownloadingReport = true;
    this.assessmentService.downloadPdfReport(this.id, this.selectedJobId)
      .pipe(takeUntil(this.destroy$))
      .subscribe({
        next: (blob) => {
          const url = URL.createObjectURL(blob);
          const anchor = document.createElement('a');
          anchor.href = url;
          const appName = (this.application?.name ?? 'Assessment').replace(/[\\/:\*?"<>|]/g, '_');
          const now = new Date();
          const dateStr = `${now.getUTCFullYear()}-${String(now.getUTCMonth() + 1).padStart(2, '0')}-${String(now.getUTCDate()).padStart(2, '0')} ${String(now.getUTCHours()).padStart(2, '0')}${String(now.getUTCMinutes()).padStart(2, '0')}`;
          anchor.download = `${appName} - Assessment Report ${dateStr}.pdf`;
          anchor.click();
          URL.revokeObjectURL(url);
          this.isDownloadingReport = false;
        },
        error: (err) => {
          console.error('Failed to download report:', err);
          this.isDownloadingReport = false;
        }
      });
  }

  onViewHistory(): void {
    if (!this.application) return;
    const openHistory = (aiJobs: AssessmentJob[]) => {
      const dialogRef = this.dialog.open(ApplicationHistoryDialogComponent, {
        width: '900px',
        disableClose: true,
        data: {
          appId: this.application!.appId,
          appName: this.application!.name,
          aiJobs,
          applicationHistory: this.applicationHistory
        }
      });
      dialogRef.afterClosed().subscribe((result: { selectedAiJobId?: number } | undefined) => {
        if (result?.selectedAiJobId) {
          const job = aiJobs.find(j => j.id === result.selectedAiJobId);
          if (job) this.onSelectHistoryJob(job);
        }
      });
    };

    if (this.trackedBusinessApplicationId) {
      this.aiAssessmentService.getJobHistoryByTrackedBusinessApplicationId(this.trackedBusinessApplicationId, true)
        .pipe(takeUntil(this.destroy$))
        .subscribe({
          next: (jobs) => openHistory(jobs),
          error: () => openHistory(this.allJobs)
        });
      return;
    }

    openHistory(this.allJobs);
  }

  onSelectHistoryJob(job: AssessmentJob): void {
    if (this.isLightweightJob(job)) {
      this.loadLightweightValidations(this.allJobs);
      return;
    }

    if (job.id === this.selectedJobId) {
      return;
    }
    this.isJobLoading = true;
    this.hasNewerAssessment = false;
    this.cdr.detectChanges();

    this.aiAssessmentService.getJob(job.id).subscribe({
      next: (jobWithResult) => {
        this.ngZone.run(() => {
          this.selectedJobId = jobWithResult.id;
          const hasResult = !!(jobWithResult?.result);
          this.viewModel = { ...this.viewModel!, job: jobWithResult, hasResult };
          this.buildScoreTiles();
          this.buildDimensionItems();
          this.isJobLoading = false;
          this.cdr.detectChanges();
          setTimeout(() => this.renderRadarChart(), 0);
        });
      },
      error: (err) => {
        console.error('[AssessmentDetail] Failed to load history job:', err);
        this.isJobLoading = false;
        this.cdr.detectChanges();
      }
    });
  }

  onViewLatestAssessment(): void {
    if (!this.trackedBusinessApplicationId) return;
    this.aiAssessmentService.getJobHistoryByTrackedBusinessApplicationId(this.trackedBusinessApplicationId, false)
      .pipe(catchError(() => of([])))
      .subscribe(jobs => {
        this.allJobs = jobs;
        const latestFinished = jobs.find(j => this.isFullCodeAssessmentJob(j) && this.isFinishedJob(j));
        if (latestFinished) {
          this.hasNewerAssessment = false;
          this.onSelectHistoryJob(latestFinished);
        }
      });
  }

  onRunCodeAssessment(): void {
    if (!this.application || (this.isEnterpriseLead && !this.isProjectLead)) return;

    this.confirmRecentRunAndOpenDialog('full');
  }

  onRunLightweightValidation(): void {
    if (!this.application || (this.isEnterpriseLead && !this.isProjectLead)) return;

    this.confirmRecentRunAndOpenDialog('lightweight');
  }

  private confirmRecentRunAndOpenDialog(mode: 'full' | 'lightweight'): void {
    if (!this.application) return;

    const trackedBusinessApplicationId = this.application.trackedBusinessApplicationId;
    if (!trackedBusinessApplicationId) {
      this.openStartAssessmentDialog(mode);
      return;
    }

    this.aiAssessmentService.getJobHistoryByTrackedBusinessApplicationId(trackedBusinessApplicationId, false)
      .pipe(catchError(() => of(this.allJobs ?? [])))
      .subscribe(jobs => {
        this.allJobs = jobs;

        const latestRecentRun = this.getLatestRecentAssessmentRun(jobs, mode);
        if (latestRecentRun) {
          const runName = mode === 'lightweight' ? 'code validation' : 'code assessment';
          const warningDialogRef = this.dialog.open(MessageDialogComponent, {
            width: '500px',
            disableClose: true,
            data: {
              title: mode === 'lightweight' ? 'Recent Code Validation' : 'Recent Code Assessment',
              message:
                `A ${runName} was already run in the last ${AssessmentDetailComponent.RECENT_ASSESSMENT_WINDOW_DAYS} days ` +
                `(latest: ${new Date(latestRecentRun.createdAt).toLocaleDateString()}). Do you want to continue?`,
              confirmMode: true,
              confirmLabel: 'Continue',
              type: 'warning'
            } as MessageDialogData
          });

          warningDialogRef.afterClosed().subscribe(confirmed => {
            if (confirmed) {
              this.openStartAssessmentDialog(mode);
            }
          });
          return;
        }

        this.openStartAssessmentDialog(mode);
      });
  }

  private getLatestRecentAssessmentRun(jobs: AssessmentJob[], mode: 'full' | 'lightweight'): AssessmentJob | null {
    const now = Date.now();
    const windowMs = AssessmentDetailComponent.RECENT_ASSESSMENT_WINDOW_DAYS * 24 * 60 * 60 * 1000;

    const recentRuns = jobs
      .filter(job => mode === 'lightweight' ? this.isLightweightJob(job) : this.isFullCodeAssessmentJob(job))
      .map(job => ({ job, createdAtMs: Date.parse(job.createdAt) }))
      .filter(item => Number.isFinite(item.createdAtMs) && (now - item.createdAtMs) <= windowMs)
      .sort((a, b) => b.createdAtMs - a.createdAtMs);

    return recentRuns.length > 0 ? recentRuns[0].job : null;
  }

  private openStartAssessmentDialog(mode: 'full' | 'lightweight'): void {
    if (!this.application) return;

    const dialogData: StartAssessmentDialogData = {
      appName: this.application.name,
      appId: this.application.appId,
      trackedBusinessApplicationId: this.application.trackedBusinessApplicationId,
      orionManifestJson: this.application.orionManifestJson,
      mode
    };

    const dialogRef = this.dialog.open(StartAssessmentDialogComponent, {
      width: '500px',
      data: dialogData,
      disableClose: true
    });

    dialogRef.afterClosed().subscribe(result => {
      if (!result || !result.jobId) {
        return; // User cancelled
      }

      // Lightweight refresh: fetch updated job list and start polling without reloading the page
      if (this.trackedBusinessApplicationId) {
        this.aiAssessmentService.getJobHistoryByTrackedBusinessApplicationId(this.trackedBusinessApplicationId, false)
          .pipe(catchError(() => of([])))
          .subscribe(jobs => {
            this.ngZone.run(() => {
              this.allJobs = jobs;
              this.hasRunningJob = true;
              this.hasNewerAssessment = false;
              this.startJobPolling();
              this.cdr.detectChanges();
            });
          });
      }
    });
  }

  onActionClick(): void {
    if (!this.application || this.isEnterpriseLead || this.isActionProcessing) return;

    this.openActionDialog();
  }

  private openActionDialog(initialState?: Partial<ActionDialogData>): void {
    if (!this.application) return;

    const dialogData: ActionDialogData = {
      application: this.application,
      rejectionReasons: this.rejectionReasons,
      isAMORETeam: this.isAMORETeam,
      currentRecommendation: this.application.outcome || '',
      currentUserEmail: this.loggedUserEmail,
      ...initialState
    };

    const dialogRef = this.dialog.open(ActionDialogComponent, {
      width: '700px',
      data: dialogData,
      disableClose: true,
      panelClass: 'action-dialog-container'
    });

    dialogRef.afterClosed().subscribe((result: ActionDialogResult | undefined) => {
      if (!result) return;

      switch (result.action) {
        case 'approve': {
          if (!this.application) break;
          this.openPatDialogForAdoAction(
            'Approve Modernization',
            'Approving modernization creates an Azure DevOps work item. Please verify or update your PAT before continuing.',
            () => this.executeApproveAssessment());
          break;
        }
        case 'override':
          if (!this.sysId) {
            this.openMessageDialog('Error', 'An error occurred. Please try again.');
            return;
          }
          if (result.overrideAction && this.application?.trackedBusinessApplicationId) {
            const overridePayload: OverrideAssessmentPayload = {
              appId: this.application.trackedBusinessApplicationId,
              overrideAction: result.overrideAction,
              overrideReason: result.overrideReason || ''
            };
            this.assignmentService.overrideAssessment(overridePayload).subscribe({
              next: () => {
                this.openMessageDialog('Success', 'The assessment has been overridden successfully.');
                this.loadAssessment(this.application!.trackedBusinessApplicationId).subscribe();
                this.loadApplicationHistory(this.application!.appId);
              },
              error: (err) => {
                console.error('Failed to save override:', err);
                this.openMessageDialog('Error', 'An error occurred. Please try again.');
              }
            });
          }
          // if (this.isAMORETeam) {
          //   this.reviewService.acceptRecommendation(this.sysId).subscribe({
          //     next: (status) => {
          //       this.isFinalized = status.isFinalized;
          //       this.finalizedBy = status.finalizedBy;
          //       this.finalizedAt = status.finalizedAt ? new Date(status.finalizedAt) : undefined;
          //       if (this.sysId) { this.loadApprovalLogs(this.sysId); }
          //       this.updateFinalDecisionLabel();
          //       this.openMessageDialog('Success', 'The assessment has been overridden successfully.');
          //     },
          //     error: (err) => {
          //       console.error('Failed to approve assessment:', err);
          //       this.openMessageDialog('Error', 'An error occurred. Please try again.');
          //     }
          //   });
          // }
          break;

        case 'assign-technical': {
          const assignDialogResult = {
            assignTo: 'Technical Owner',
            email: result.assignToEmail || '',
            action: '',
            selectedPodId: null,
            notes: result.notes || '',
            consentProvided: false
          };
          this.proceedWithAssignment(assignDialogResult);
          break;
        }

        case 'assign-amore': {
          if (result.selectedPodId) {
            this.proceedWithAssignment({
              assignTo: result.selectedPodName || '',
              email: '',
              selectedPodId: result.selectedPodId,
              notes: result.notes || '',
            });
          } else {
            this.openPatDialogForAdoAction(
              'Assign to AMORE Team',
              'Assigning to AMORE Team can create an Azure DevOps feature. Please verify or update your PAT before continuing.',
              () => this.proceedWithAssignment({
                assignTo: Role.AMORETeam,
                email: result.assignToEmail || '',
                selectedPodId: null,
                notes: result.notes || '',
              }),
              () => this.openActionDialog({
                initialStep: 3,
                initialSelectedAction: 'assign-amore',
                initialAmoreAssignType: result.assignToEmail
                  ? (result.assignToEmail === this.loggedUserEmail ? 'myself' : 'architect')
                  : 'myself',
                initialAssignEmail: result.assignToEmail,
                initialAssignNotes: result.notes,
                initialConsentProvided: true
              })
            );
          }
          break;
        }

        case 'assign-pod': {
          const podAssignResult = {
            assignTo: result.selectedPodName || '',
            email: result.assignToEmail || '',
            selectedPodId: result.selectedPodId ?? null,
            notes: result.notes || '',
          };
          this.proceedWithAssignment(podAssignResult);
          break;
        }

        case 'reject': {
          const rejectionResult = {
            rejectionReasonId: result.rejectionReasonId!,
            rejectionReason: result.rejectionReason || '',
            otherReasonText: result.otherReasonText || ''
          };
          this.proceedWithRejection(rejectionResult);
          break;
        }
      }
    });
  }

  private openPatDialogForAdoAction(
    title: string,
    intro: string,
    onProceed: () => void,
    onBackToAction?: () => void): void {
    const patDialogRef = this.dialog.open(PatUpdateDialogComponent, {
      width: '700px',
      disableClose: true,
      data: { title, intro }
    });

    patDialogRef.afterClosed().subscribe((patResult: PatUpdateDialogResult | undefined) => {
      if (!patResult?.proceed || !this.application) {
        onBackToAction?.();
        return;
      }

      onProceed();
    });
  }

  private executeApproveAssessment(): void {
    if (!this.application) return;

    const approvePayload: ApproveAssessmentPayload = {
      appId: this.application.appId,
      trackedBusinessApplicationId: this.application.trackedBusinessApplicationId,
      consentTimestamp: new Date()
    };

    this.startActionProcessing('Creating ADO feature and generating PDF...');
    this.assignmentService.approveAssessment(approvePayload)
      .pipe(finalize(() => this.stopActionProcessing()))
      .subscribe({
      next: (response: ApproveAssessmentResult) => {
        const baseMessage = 'The assessment has been approved successfully.';
        const workItemMessage = response?.adoWorkItemUrl
          ? (response?.adoWorkItemOperation === 'updated'
            ? `Azure DevOps work item updated: ${response.adoWorkItemUrl}`
            : `Azure DevOps work item created: ${response.adoWorkItemUrl}`)
          : null;

        const message = workItemMessage
          ? `${baseMessage}\n\n${workItemMessage}`
          : baseMessage;

        this.openMessageDialog('Success', message, response?.adoWorkItemUrl ?? undefined);
        this.loadAssessment(this.application!.trackedBusinessApplicationId).subscribe();
        this.loadApplicationHistory(this.application!.appId);
      },
      error: (err) => {
        console.error('Failed to approve assessment:', err);
        this.openMessageDialog('Error', 'An error occurred. Please try again.');
      }
    });
  }
}
